import { memo, PropsWithChildren, useCallback } from 'react';
import classNames from 'classnames';

import { CheckboxSize } from '@xemplo/checkbox';
import { ListReservedValues } from '@xemplo/stateful-list-core';

import { useMultiSelect } from '../../utils/hooks/use-multi-select';
import { ListItemProps } from '../../utils/types/stateful-list.types';
import * as S from '../common/common.styles';

import { CustomCheckbox } from './list-item.styles';

export const ListItemMulti = memo(
  ({ children, className, item }: PropsWithChildren<ListItemProps>) => {
    const { activeDescendantId, isNoResult, isSelected, toggleItem } = useMultiSelect();
    const { focusId, value, icon: Icon, itemCallback } = item;
    const selected = isSelected(value);

    const handleClick = useCallback(
      (e?: React.MouseEvent) => {
        // When clicking on the checkbox, we want to prevent the click from bubbling up
        // to the list item, which would toggle the item again.
        // This is necessary to avoid double toggling when clicking the checkbox.
        e?.stopPropagation();
        e?.preventDefault();
        if (value === ListReservedValues.noResult) return;

        toggleItem(value);
        itemCallback?.(value);
      },
      [itemCallback, toggleItem, value],
    );

    return (
      <S.ListItem
        id={focusId}
        className={classNames({ selected, 'no-result': isNoResult }, className)}
        onClick={handleClick}
        tabIndex={activeDescendantId === focusId ? 0 : -1}
      >
        {Icon && <Icon />}
        {children}
        {!isNoResult && (
          <CustomCheckbox
            value={value}
            ariaLabel={`selected item for ${value}`}
            checked={selected}
            size={CheckboxSize.Small}
          />
        )}
      </S.ListItem>
    );
  },
);

export default ListItemMulti;
